"use client";

import { useEffect, useMemo, useState } from "react";
import {
  CopyButton,
  Notice,
  SelectField,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  CARD,
  HINT,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  buildAllQueries,
  type Concept,
  conceptsFromQuery,
  DATABASES,
  genericQuery,
  type SearchField,
} from "@/lib/tool-kit/research-workflow/boolean";

/** English strings. A translated content file overrides these through `ui`. */
export const BOOLEAN_SEARCH_BUILDER_STRINGS = {
  conceptsHeading: "Concepts",
  conceptsHint:
    "One concept per row. Synonyms inside a row are joined with OR; the rows are joined with AND.",
  syntaxHint:
    "Multi-word terms are quoted for you. Add * to truncate — nurs* finds nurse, nurses and nursing. For proximity, put ~ and a number between two words: sleep ~3 deprivation.",
  conceptLabel: "Concept {number} — synonyms, one per line",
  conceptPlaceholder: "e.g. sleep quality\ninsomnia\nsleep ~3 deprivation",
  fieldLabel: "Search in",
  fieldOptions: {
    any: "Default fields (keywords, topic)",
    title: "Title",
    titleAbstract: "Title or abstract",
    subject: "Subject headings (MeSH, thesaurus)",
  },
  removeConcept: "Remove",
  removeConceptLabel: "Remove concept {number}",
  addConcept: "Add a concept",
  loadExample: "Load an example",
  clearAll: "Clear",
  excludeLabel: "Exclude with NOT — one term per line (optional)",
  excludePlaceholder: "e.g. child*",
  exampleConcepts: [
    "sleep quality\ninsomnia\nsleep ~3 deprivation",
    "student*\nundergraduate*\ncollege students",
  ],
  exampleExclude: "child*",
  resultsHeading: "Your search strings",
  logicLabel: "The logic",
  emptyPrompt:
    "Type at least one term into a concept to build the search strings.",
  databases: {
    scholar: "Google Scholar",
    pubmed: "PubMed",
    ebsco: "EBSCOhost — CINAHL, APA PsycInfo, Academic Search and more",
    proquest: "ProQuest",
    wos: "Web of Science",
    scopus: "Scopus",
  },
  characters: "one:{count} character|other:{count} characters",
  copy: "Copy",
  copied: "Copied!",
  copyAll: "Copy all strings",
  notes: {
    scholarNoTruncation:
      "Google Scholar has no truncation, so the asterisks were removed — add the word forms you need as separate terms.",
    scholarNoProximity:
      "Google Scholar has no proximity operator, so each pair became an exact phrase.",
    scholarNoField:
      "Google Scholar can limit to titles (intitle:) but not to abstracts or subject headings, so those rows search everywhere.",
    scholarLength:
      "Longer than 256 characters — Google Scholar cuts the query off there. Drop the least useful synonyms.",
    pubmedShortStem:
      "PubMed ignores an asterisk with fewer than four letters before it — nur* searches only for “nur”.",
    pubmedProximityField:
      "PubMed proximity works only in titles and abstracts, so those pairs search Title/Abstract.",
    pubmedProximityWildcard:
      "PubMed proximity searches do not accept wildcards, so the asterisks were removed there.",
    ebscoXb:
      "In the EBSCO databases that support it — CINAHL, MEDLINE, APA PsycInfo and Academic Search among them — the XB code searches title and abstract in one go.",
    proquestTruncation:
      "In ProQuest, * stands for up to five characters; write [*n] to allow more, as in educat[*8].",
    wosNoSubject:
      "The Web of Science Core Collection has no subject thesaurus, so subject rows search Topic (TS=).",
    scopusAndNot:
      "Scopus writes NOT as AND NOT and expects it at the end of the string.",
  },
} as const;

type Strings = typeof BOOLEAN_SEARCH_BUILDER_STRINGS;

const MAX_CONCEPTS = 8;

const FIELDS: readonly SearchField[] = [
  "any",
  "title",
  "titleAbstract",
  "subject",
];

const INITIAL: Concept[] = [
  { id: "c1", terms: "", field: "any" },
  { id: "c2", terms: "", field: "any" },
];

function QueryCard({
  name,
  query,
  notes,
  strings,
  count,
}: {
  name: string;
  query: string;
  notes: readonly string[];
  strings: Strings;
  count: string;
}) {
  return (
    <div className={`${CARD} space-y-3`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className={WIDGET_H3}>{name}</h3>
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copy}
          small
          text={query}
        />
      </div>
      <pre className="whitespace-pre-wrap break-words rounded-md bg-muted/40 p-3 font-mono text-foreground text-sm">
        {query}
      </pre>
      <p className={HINT}>{count}</p>
      {notes.length > 0 ? (
        <ul className="list-disc space-y-1 pl-5 text-muted-foreground text-sm">
          {notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export default function BooleanSearchBuilderWidget() {
  const strings = useToolUi(BOOLEAN_SEARCH_BUILDER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [concepts, setConcepts] = useState<Concept[]>(INITIAL);
  const [exclude, setExclude] = useState("");
  const [nextId, setNextId] = useState(3);

  // Terms handed over in the link (from the PICO Question Generator), read after mount.
  useEffect(() => {
    const groups = conceptsFromQuery(window.location.search);
    if (groups.length > 0) {
      setConcepts(
        groups.map((terms, index) => ({
          id: `u${index + 1}`,
          terms,
          field: "any",
        }))
      );
    }
  }, []);

  const update = (id: string, patch: Partial<Concept>) =>
    setConcepts((current) =>
      current.map((concept) =>
        concept.id === id ? { ...concept, ...patch } : concept
      )
    );

  const addConcept = () => {
    setConcepts((current) => [
      ...current,
      { id: `c${nextId}`, terms: "", field: "any" },
    ]);
    setNextId((id) => id + 1);
  };

  const loadExample = () => {
    setConcepts(
      strings.exampleConcepts.map((terms, index) => ({
        id: `e${index + 1}`,
        terms,
        field: index === 0 ? "titleAbstract" : "any",
      }))
    );
    setExclude(strings.exampleExclude);
  };

  const clearAll = () => {
    setConcepts(INITIAL);
    setExclude("");
  };

  const queries = useMemo(
    () => buildAllQueries({ concepts, exclude }),
    [concepts, exclude]
  );
  const logic = genericQuery(concepts.map((concept) => concept.terms));
  const allText = DATABASES.filter((db) => queries[db].query)
    .map((db) => `${strings.databases[db]}\n${queries[db].query}`)
    .join("\n\n");

  return (
    <div className="space-y-6">
      <section className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.conceptsHeading}</h2>
        <Notice>
          {strings.conceptsHint} {strings.syntaxHint}
        </Notice>
        {concepts.map((concept, index) => {
          const number = fmt(index + 1);
          return (
            <div className={`${CARD} space-y-3`} key={concept.id}>
              <TextAreaField
                id={`bsb-terms-${concept.id}`}
                label={t(strings.conceptLabel, { number })}
                onChange={(value) => update(concept.id, { terms: value })}
                placeholder={strings.conceptPlaceholder}
                rows={4}
                value={concept.terms}
              />
              <div className="flex flex-wrap items-end gap-3">
                <div className="min-w-[14rem] flex-1">
                  <SelectField<SearchField>
                    id={`bsb-field-${concept.id}`}
                    label={strings.fieldLabel}
                    onChange={(value) => update(concept.id, { field: value })}
                    options={FIELDS.map((field) => ({
                      value: field,
                      label: strings.fieldOptions[field],
                    }))}
                    value={concept.field}
                  />
                </div>
                <button
                  aria-label={t(strings.removeConceptLabel, { number })}
                  className={BUTTON_SMALL}
                  disabled={concepts.length <= 1}
                  onClick={() =>
                    setConcepts((current) =>
                      current.filter((item) => item.id !== concept.id)
                    )
                  }
                  type="button"
                >
                  {strings.removeConcept}
                </button>
              </div>
            </div>
          );
        })}
        <TextAreaField
          id="bsb-exclude"
          label={strings.excludeLabel}
          onChange={setExclude}
          placeholder={strings.excludePlaceholder}
          rows={2}
          value={exclude}
        />
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            disabled={concepts.length >= MAX_CONCEPTS}
            onClick={addConcept}
            type="button"
          >
            {strings.addConcept}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={loadExample}
            type="button"
          >
            {strings.loadExample}
          </button>
          <button className={BUTTON_SECONDARY} onClick={clearAll} type="button">
            {strings.clearAll}
          </button>
        </div>
      </section>

      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
        {logic ? (
          <>
            <div className={`${RESULT_CARD} space-y-2`}>
              <p className="font-medium text-foreground text-sm">
                {strings.logicLabel}
              </p>
              <p className="break-words font-mono text-foreground text-sm">
                {logic}
              </p>
            </div>
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copyAll}
              text={allText}
            />
            {DATABASES.map((db) => {
              const { query, notes } = queries[db];
              const length = query.length;
              return (
                <QueryCard
                  count={tPlural(strings.characters, locale, length, {
                    count: fmt(length),
                  })}
                  key={db}
                  name={strings.databases[db]}
                  notes={notes.map((note) => strings.notes[note])}
                  query={query}
                  strings={strings}
                />
              );
            })}
          </>
        ) : (
          <Notice>{strings.emptyPrompt}</Notice>
        )}
      </section>
    </div>
  );
}
